'use client'

import { isSourceUrl } from '@repo/core/material'
import { Input } from '@repo/ui'

import { OrderField } from '@/components/sections/order/order-field.client'
import { type CreatorOfferingView, type CreatorPick, sourceRequired } from '@/lib/creator'
import { SOURCE_URL_MAX_CHARS } from '@/lib/order-line'

/**
 * "Link do materiału" — spec §7's source footage, on the line it belongs to.
 *
 * A LINK and never an upload, and that is the spec's own decision ("bez uploadu
 * plików"): a montage job is tens of gigabytes, and a product that stored them
 * would be a storage business with an editing side-line. What the customer
 * already has is a Drive folder or a WeTransfer, and asking for that is asking
 * for nothing new.
 *
 * Drawn only when `sourceMode` says so, like every other control in this
 * directory. The validity hint appears only once the box is non-empty and
 * wrong — a red field before anybody has finished typing is a scold, not help.
 */
export function SourceLinkField({
  offering,
  pick,
  lineId,
  problem,
  onPatch,
}: {
  offering: CreatorOfferingView
  pick: CreatorPick
  /** Scopes the control id: the same offering can be in the order twice. */
  lineId: string
  problem?: string | null
  onPatch: (next: Partial<CreatorPick>) => void
}) {
  const required = sourceRequired(offering, pick)
  const typed = pick.sourceUrl.trim()
  const broken = typed.length > 0 && !isSourceUrl(typed)

  return (
    <OrderField
      wide
      htmlFor={`source-${lineId}`}
      label={
        <>
          Link do materiału
          {required ? null : <span className="font-normal text-white/55"> — opcjonalnie</span>}
        </>
      }
      problem={problem}
      hint={
        broken
          ? 'To nie wygląda na link — wklej cały adres razem z https://.'
          : 'Google Drive, WeTransfer, Dropbox — cokolwiek już masz. Upewnij się, że dostęp jest otwarty.'
      }
    >
      <Input
        id={`source-${lineId}`}
        type="url"
        inputMode="url"
        value={pick.sourceUrl}
        onChange={(event) => onPatch({ sourceUrl: event.target.value })}
        maxLength={SOURCE_URL_MAX_CHARS}
        required={required}
        aria-invalid={broken}
        placeholder="https://drive.google.com/… albo https://we.tl/…"
        className="min-h-12 rounded-2xl border-white/15 bg-black/40 text-[0.95rem] text-white placeholder:text-white/55"
      />
    </OrderField>
  )
}
